import React, { useEffect, useState } from "react";
import { Container, Button, Dropdown, Spinner, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Eye } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../Styles/yellowcalfcss/OtherIncomes.css";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import ConfirmModal from "../../components/ConfirmModal";

export default function OtherIncomes() {
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
const [isDeleting, setIsDeleting] = useState(false);
const [deleteId, setDeleteId] = useState(null);

  const [filters, setFilters] = useState({
    status_search: "",
    name_search: "",
    date_search: "",
    amount_search: "",
  });

  /* ---------------- FETCH INCOME LIST ---------------- */
  useEffect(() => {
    fetchIncomeList();
  }, [filters]);

  const fetchIncomeList = async () => {
    setLoading(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",

      // filters
      status_search: filters.status_search,
      name_search: filters.name_search,
      date_search: filters.date_search,
      amount_search: filters.amount_search,
    };

    try {
      const response = await simplePostCall(
        ApiConfig.incomeList,
        JSON.stringify(requestBody)
      );

      if (response?.result && Array.isArray(response.data)) {
        setIncomeData(response.data);
      } else {
        setIncomeData([]);
      }
    } catch (error) {
      console.error("Income list error:", error);
      setIncomeData([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- EXPORT ---------------- */
  const handleExport = (type) => {
    if (type === "json") {
      const dataStr = JSON.stringify(incomeData, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "income-data.json";
      link.click();
      toast.success("Exported as JSON");
    }

    if (type === "pdf") {
      const input = document.getElementById("data-capture-wrapper");
      html2canvas(input).then((canvas) => {
        const pdf = new jsPDF();
        const imgData = canvas.toDataURL("image/png");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save("income-data.pdf");
        toast.success("Exported as PDF");
      });
    }
  };

  /* ---------------- DELETE ---------------- */
const handleDeleteIncome = async () => {
  if (!deleteId) return;

  setIsDeleting(true);

  const requestBody = {
    customer_id: Number(localStorage.getItem("customer_id")),
    api_key: localStorage.getItem("api_key"),
    user_id: Number(localStorage.getItem("user_id")),
    language: "en",
    id: Number(deleteId),
  };

  try {
    const response = await simplePostCall(
      ApiConfig.incomeDelete,
      JSON.stringify(requestBody)
    );

    if (response?.result) {
      toast.success(response.message || "Income deleted successfully");
      setShowDeleteModal(false);
      fetchIncomeList(); // 🔄 refresh list
    } else {
      toast.error(response?.message || "Failed to delete income");
    }
  } catch (error) {
    console.error("Income delete error:", error);
    toast.error("Network error occurred");
  } finally {
    setIsDeleting(false);
  }
};

  /* ---------------- UI ---------------- */
  return (
    <div className="wrap-finances" style={{margin: '0px -10px 0px -10px'}}>
        <ToastContainer position="top-right" autoClose={3000} />

      <Container fluid>
        {/* HEADER */}
        <div className="top-section-utilities">
          <div className="topbar-finances-flex">
            <h3 className="heading-finance-title">Income</h3>

            <div className="control-buttons-zone" style={{ marginTop: "5px" }}>
              {/* <Button
                className="button-finance-option"
                onClick={() => navigate("/accountant/BankType")}
              >
                Bank Details
              </Button> */}
              <Button
                className="button-finance-option"
                onClick={() => navigate("/accountant/IncomeType")}
              >
                Income Type
              </Button>
              <Button
                className="button-finance-option"
                onClick={() => navigate("/accountant/CreateIncome")}
              >
                Record Income
              </Button>

              <Dropdown>
                <Dropdown.Toggle className="button-finance-option">
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleExport("pdf")}>
                    Save as PDF
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExport("json")}>
                    Save as JSON
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* FILTERS */}
        <div className="table-top-filters d-flex gap-3 mb-3" style={{margin: '0px -2px 0px -2px'}}>
          <Form.Control
            placeholder="Name"
            value={filters.name_search}
            onChange={(e) =>
              setFilters({ ...filters, name_search: e.target.value })
            }
          />
          <Form.Control
            placeholder="Status"
            value={filters.status_search}
            onChange={(e) =>
              setFilters({ ...filters, status_search: e.target.value })
            }
          />
          <Form.Control
            type="date"
            value={filters.date_search}
            onChange={(e) =>
              setFilters({ ...filters, date_search: e.target.value })
            }
          />
          <Form.Control
            placeholder="Amount"
            value={filters.amount_search}
            onChange={(e) =>
              setFilters({ ...filters, amount_search: e.target.value })
            }
          />
        </div>

        {/* TABLE */}
        <div id="data-capture-wrapper">
          <div className="table-header-wrapper-card" style={{ height: "65px" }}>
            <div
              className="grid-structure-headers"
              style={{
                textAlign: "center",
                marginLeft: "15px",
                marginTop: "9px",
                fontWeight: "bold",
              }}
            >
              <div className="cell-index-number">Sr No</div>
              <div className="cell-bill-id">Income Type</div>
              <div className="cell-party-name">Account</div>
              <div className="cell-date-entry">Date</div>
              <div className="cell-date-due">Amount</div>
              <div className="cell-amount-total">Status</div>
              <div className="cell-action-icons">Actions</div>
            </div>
          </div>

          <div className="records-entries-list">
            {loading ? (
              <div className="text-center p-4">
                <Spinner />
              </div>
            ) : incomeData.length === 0 ? (
              <div className="text-center p-4">No data found</div>
            ) : (
              incomeData.map((item, index) => (
                <div key={item.income_id} className="row-record-block">
                  <div
                    className="grid-structure-headers"
                    style={{ textAlign: "center", fontSize: "14px" }}
                  >
                    <div className="cell-index-number">{index + 1}</div>
                    <div className="cell-bill-id">
                      {item.income_type_name}
                    </div>
                    <div className="cell-party-name">
                      {item.income_type_account_name}
                    </div>
                    <div className="cell-date-entry">
                      {item.income_date}
                    </div>
                    <div className="cell-date-due">
                      {item.income_amount}
                    </div>
                    <div className="cell-amount-total">
                      {item.income_status}
                    </div>
                    <div className="cell-action-icons">
                      <button
                        className="btn-inline-icon"
                        onClick={() =>
                          navigate(`/accountant/ViewIncome/${item.income_id}`)
                        }
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="btn-inline-icon"
                        onClick={() =>
                          navigate(`/accountant/EditIncome/${item.income_id}`)
                        }
                      >
                        <Edit size={16} />
                      </button>
                     <button
  className="btn-inline-icon"
  onClick={() => {
    setDeleteId(item.income_id);
    setShowDeleteModal(true);
  }}
>
  <Trash2 size={16} />
</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="section-footer- d-flex justify-content-between">
          <p className="text-showing-counts">
            Showing {incomeData.length} items
          </p>
        </div>
      </Container>
      <ConfirmModal
  show={showDeleteModal}
  onHide={() => setShowDeleteModal(false)}
  title="Delete Income"
  message="Are you sure you want to delete this income record?"
  confirmText="Delete"
  confirmVariant="danger"
  onConfirm={handleDeleteIncome}
  isLoading={isDeleting}
/>
    </div>
  );
}
